import { captureException } from "@sentry/react";
import { z } from "zod/v4-mini";

import { camelCaseObject } from "@/utils/caseUtils.ts";
import { zodValidation } from "@/utils/zodValidationUtils.ts";

export type Schema<Output, Input = unknown> = z.ZodMiniType<Output, Input>;

type BaseZodError = {
  issues: z.core.$ZodIssueBase[];
};

export type Result<T, E> = { status: "success"; data: T } | { status: "error"; error: E };

/**
 * Safely parses and validates a value using a provided Zod schema.
 * z.schema().safeParse is not actually safe (because of transform)
 *
 * This function attempts to parse a given value using the specified Zod schema.
 * If parsing is successful, it returns an object with a "success" status and the parsed value.
 * If validation fails due to a Zod error, the error is logged, and an object with an "error" status and the Zod error details is returned.
 * If any other exception occurs during the process, it returns an error result with a generic transformation exception.
 *
 * @template T - The type of the Zod schema being used for parsing.
 * @param schema - The Zod schema to use for validation and type safety.
 * @returns A result object representing either the parsed data on success, or the error details on failure.
 */
export const zodSafeParse =
  <T extends z.ZodMiniType>(schema: T) =>
  (value: unknown): Result<z.output<T>, BaseZodError> => {
    try {
      const parsed = schema.parse(value);
      return { status: "success", data: parsed };
    } catch (e) {
      if (e instanceof z.core.$ZodError) {
        console.warn(z.prettifyError(e));
        return { status: "error", error: e };
      }
      return {
        status: "error",
        error: {
          issues: [
            {
              code: "transform_exception",
              input: value,
              message: "Exception while transforming value",
              path: [],
            },
          ],
        },
      };
    }
  };

/**
 * Represents a schema definition for an identifier.
 *
 * This variable is a function that leverages the Zod library to create a schema
 * which coerces input into a string data type.
 *
 * The main use of `idSchema` is to only manipulate string ids instead of both numbers and strings.
 *
 * @returns A Zod Schema that transforms an input into a string
 */
export const idSchema = () => z.coerce.string();

/**
 * A Zod schema helper function that creates a transformation pipeline to ignore
 * any input and return `undefined` as the transformation result.
 *
 * This function uses `z.unknown()` to accept any type as input and applies a
 * transform step using `z.transform()` to always output `undefined`. It can be useful
 * in cases where certain input fields or values are intentionally ignored.
 *
 * @returns A Zod Schema that always returns `undefined`
 */
export const zodIgnore = () => zodTransform(z.unknown(), () => undefined);

/**
 * A utility function that creates a Zod schema allowing optional values.
 * The schema accepts `undefined` or `null` as valid values and transforms them into `undefined`,
 * while validating and preserving any other value based on the provided schema.
 *
 * @template T - A subtype of `ZodMiniType` which defines the validation rules for the schema.
 * @param {T} schema - The base Zod schema defining the structure and rules for valid values.
 * @returns  A transformed Zod schema that allows optional values and adheres to the provided schema.
 */
export const zodOptional = <T extends z.ZodMiniType>(schema: T) =>
  zodTransform(z.nullish(schema), (data): z.output<T> | undefined => {
    if (data === undefined || data === null) {
      return undefined;
    }
    return data;
  });

/**
 *
 * A utility function to parse an array.
 * Validate an array like z.array, but with a few differences:
 * - If the value is not an array, it will return an empty array.
 * - If an item is invalid, it will be ignored.
 *
 * @template T - A type extending ZodMiniType, representing the schema for validation.
 * @param {T} schema - The Zod schema defining the validation rules for each array item.
 * @returns - A Zod pipe that enforces validation and returns an array of valid items.
 */
export const zodForcedArray = <T extends z.ZodMiniType>(schema: T) =>
  zodTransform(z.unknown(), (data): z.output<T>[] => {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.reduce((acc, item) => {
      const parsed = zodSafeParse(schema)(item);
      if (parsed.status === "success") {
        acc.push(parsed.data);
      } else {
        console.warn(parsed.error);
        captureException(parsed.error);
      }
      return acc;
    }, [] as T[]);
  });

/**
 * A function that creates a Zod schema object with camel-cased transformation applied.
 *
 * @template T - Extends the ZodLooseShape type, representing the shape definition for the object schema.
 * @param {T} shape - The schema definition for the object, specifying the expected keys and types.
 * @param [params] - Optional parameters for additional configuration.
 * @param [params.pipe] - A pipe for additional schema transformation or validation.
 * @param [params.object] - Parameters to customize the behavior of the Zod object schema.
 * @returns A Zod pipeline that composes an object schema, applies camel-case key transformation, and optionally applies a provided pipe.
 */
export const zodCamelCaseObject = <T extends z.core.$ZodLooseShape>(
  shape: T,
  params?: string | z.core.$ZodObjectParams,
) => zodTransform(z.object(shape, params), camelCaseObject);

/**
 * Transforms the keys of an object validated by the given Zod schema into camelCase.
 *
 * @template T - The type extending ZodMiniType.
 * @param {T} schema - A Zod schema to validate the object.
 * @returns A Zod pipe that validates the object using the provided schema and transforms its keys into camelCase.
 */
export const zodCamelCase = <T extends z.ZodMiniType>(schema: T) => zodTransform(schema, camelCaseObject);

/**
 * Creates a Zod schema for validating ISO 8601 compliant date-time strings.
 *
 * The generated schema validates strings that represent date-time values
 * in ISO 8601 format, optionally with time zone offset and support for local time.
 * Additional validation parameters can be provided as an argument to customize the behavior.
 *
 * @param [params] - Optional parameters to customize the validation schema.
 * These parameters can include additional constraints or special rules for
 * ISO 8601 date-time string validation.
 *
 * @returns A Zod schema configured to validate ISO 8601 date-time strings
 * with support for offsets and local time.
 */
export const zodDateTime = (params?: z.core.$ZodISODateTimeParams) =>
  z.iso.datetime({ offset: true, local: true, ...params });

/**
 * A utility function that creates a Zod schema allowing a literal value from a given array of strings
 * or optionally a transformed string input that resolves to `undefined`.
 *
 * This function accepts an array of strings as the allowed literal values and creates
 * a Zod union schema. The union consists of:
 * 1. `z.literal` schema for exact matching of the provided literal value.
 * 2. A pipeline schema that accepts any string input and transforms it into `undefined`.
 *
 * @param value An array of string values to be used as the permissible literal values.
 * @param params Optional parameters for configuring the Zod literal schema.
 * @returns A Zod union schema combining a literal validator and a transformed string-to-undefined validator.
 */
export const zodPermissiveLiteral = <const T extends string[]>(value: T, params?: z.core.$ZodLiteralParams) =>
  z.union([z.literal(value, params), zodTransform(z.string(), () => undefined)]);

/**
 * Handles axios NoContent Policy.
 * When a 204 no content is received, response.data is ''
 * This transforms '' to null and otherwise keeps the schema
 *
 * @param schema - The base schema to extend.
 * @returns  A ZodEffects pipeline where the empty string is transformed to `null`,
 * and valid schema values are retained unchanged.
 */
export const zodNoContent = <T extends z.ZodMiniType>(schema: T) =>
  z.union([schema, zodTransform(z.literal(""), data => (data === "" ? null : data))]);

/**
 * Syntax sugar around z.pipe(..., z.transform(...))
 *
 * @param schema
 * @param transform
 */
export const zodTransform = <I extends z.ZodMiniType, O>(
  schema: I,
  transform: (data: z.output<I>, ctx: z.core.ParsePayload) => O,
) => z.pipe(schema, z.transform(transform));

export const zodPermissivePhone = (params?: {
  string?: string | z.core.$ZodStringParams;
  refine?: string | z.core.$ZodCustomParams;
}) =>
  z
    .string(params?.string)
    .check(z.refine(data => data.replaceAll(" ", "").match(/^\+?[0-9]{10,}$/) !== null, params?.refine));

/**
 * Custom zod
 * z => cz
 */
export const cz = {
  safeParse: zodSafeParse,
  id: idSchema,
  ignore: zodIgnore,
  optional: zodOptional,
  forcedArray: zodForcedArray,
  camelCaseObject: zodCamelCaseObject,
  camelCase: zodCamelCase,
  datetime: zodDateTime,
  permissiveLiteral: zodPermissiveLiteral,
  noContent: zodNoContent,
  transform: zodTransform,
  phone: zodPermissivePhone,
  validation: zodValidation,
};
